import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, Checkbox, Avatar, Typography, Space, Badge, Row, Col, message, Collapse, Empty, Spin } from "antd";
import { SearchOutlined, ApartmentOutlined, SaveOutlined, TeamOutlined } from "@ant-design/icons";
import { InputField, AntdButton } from "../../../blueplanitUtils/InputField";
import { updateEmployee } from "../../../Slices/Thunk/employeeThunk";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const filterActiveEmployees = (emps) => emps?.filter((e) => e.employeestatus === "Active" && e.client?.length > 0) || [];

export default function ClientConfidentialAccess() {
  const dispatch = useDispatch();
  const { AllEmployees: allEmployees } = useSelector((s) => s.employee);
  const { saving } = useSelector((s) => s.employeeAccess);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [initialClientAccessMap, setInitialClientAccessMap] = useState({});
  const [clientAccessMap, setClientAccessMap] = useState({});

  // Memoized data processing
  const employees = useMemo(() => filterActiveEmployees(allEmployees), [allEmployees]);

  const selectedEmp = useMemo(() =>
    employees.find((e) => e.id === selectedEmployee),
    [employees, selectedEmployee]
  );
  useEffect(() => {
    if (!selectedEmp) return;

    const map = {};
    selectedEmp.client.forEach(c => {
      map[c.NameOfClient] = Boolean(c.confidentialAcess);
    });

    setClientAccessMap(map);
    setInitialClientAccessMap(map)
  }, [selectedEmp]);

  // Optimized filtered employees with grouped structure by department
  const groupedFilteredEmployees = useMemo(() => {
    const filtered = !searchTerm ? employees : employees.filter((e) => {
      const lower = searchTerm.toLowerCase();
      return e.firstname?.toLowerCase().includes(lower) ||
        e.lastname?.toLowerCase().includes(lower) ||
        e.department?.toLowerCase().includes(lower);
    });

    const grouped = {};
    filtered.forEach((e) => {
      const dept = e.department || 'No Department';
      if (!grouped[dept]) grouped[dept] = [];
      grouped[dept].push(e);
    });
    return grouped;
  }, [searchTerm, employees]);

  // Toggle entire client access
  const toggleClientAccess = useCallback((empId, clientName, value) => {
    setClientAccessMap(prev => ({
      ...prev,
      [clientName]: value
    }));
  }, []);

  // Save changes
  const handleSave = useCallback(async () => {
    if (!selectedEmp) {
      messageApi.error("Please select an employee before saving");
      return;
    }

    // 🔍 Check if anything changed
    const hasChanges = Object.keys(clientAccessMap).some(
      (clientName) =>
        clientAccessMap[clientName] !== initialClientAccessMap[clientName]
    );

    if (!hasChanges) {
      messageApi.error("No changes detected. Please modify access before saving.");
      return;
    }

    const clientUpdatePayload = {
      id: selectedEmp.id,
      input: {
        client: selectedEmp.client.map(c => ({
          NameOfClient: c.NameOfClient,
          SlotNo: c.SlotNo,
          processes: c.processes,
          role: c.role,
          confidentialAcess: clientAccessMap[c.NameOfClient]
        }))
      }
    };
    try {
      const response = await dispatch(updateEmployee(clientUpdatePayload));
      messageApi.success("Client confidential access updated successfully");

      // 🔄 Reset initial state after successful save
      setInitialClientAccessMap(clientAccessMap);
    } catch (error) {
      messageApi.error("Failed to update client confidential access");
    }
  }, [selectedEmp, clientAccessMap,initialClientAccessMap]);

  return (
    <div style={styles.container}>
      {contextHolder}

      <Header />

      <Row gutter={20}>
        <Col span={8}>
          <EmployeeList
            groupedEmployees={groupedFilteredEmployees}
            selectedEmployee={selectedEmployee}
            onEmployeeSelect={setSelectedEmployee}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </Col>

        <Col span={16}>
          <EmployeeAccessPanel
            selectedEmployee={selectedEmp}
            selectedEmployeeId={selectedEmployee}
            onToggleClientAccess={toggleClientAccess}
            clientAccessMap={clientAccessMap}
          />
        </Col>
      </Row>

      <SaveFooter onSave={handleSave} saving={saving} />
    </div>
  );
}

// Optimized Sub-components
const Header = React.memo(() => (
  <div style={styles.headerRow}>
    <div>
      <Title level={3} style={{ margin: 0, marginBottom: 4 }}>
        Client Confidential Access Management
      </Title>
      <Text type="secondary">Manage which employees can view confidential client data</Text>
    </div>
  </div>
));

const EmployeeList = React.memo(({ groupedEmployees, selectedEmployee, onEmployeeSelect, searchTerm, onSearchChange }) => (
  <Card
    style={styles.listCard}
    bodyStyle={{ padding: 16 }}
    title={
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <TeamOutlined />
          <span>Select Employee</span>
        </div>
        <InputField
          placeholder="Search employees..."
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          style={{ width: "100%" }}
          size="middle"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          allowClear
        />
      </div>
    }
  >
    <div style={{ maxHeight: 600, overflowY: "auto" }}>
      {Object.keys(groupedEmployees).length === 0 ? (
        <Empty description="No employees found" style={{ padding: "40px 0" }} />
      ) : (
        <Collapse ghost defaultActiveKey={Object.keys(groupedEmployees)}>
          {Object.keys(groupedEmployees).map((deptName) => (
            <Panel
              header={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong>{deptName}</Text>
                  <Badge
                    count={groupedEmployees[deptName].length}
                    style={{ backgroundColor: "#1677ff" }}
                  />
                </div>
              }
              key={deptName}
            >
              {groupedEmployees[deptName].map((emp) => (
                <EmployeeListItem
                  key={emp.id}
                  employee={emp}
                  isSelected={selectedEmployee === emp.id}
                  onClick={() => onEmployeeSelect(emp.id)}
                />
              ))}
            </Panel>
          ))}
        </Collapse>
      )}
    </div>
  </Card>
));

const EmployeeAccessPanel = React.memo(({
  selectedEmployee,
  selectedEmployeeId,
  onToggleClientAccess,
  clientAccessMap
}) => (
  <Card
    style={styles.detailCard}
    bodyStyle={{ padding: 0 }}
    title={
      selectedEmployee ? (
        <EmployeeHeader
          employee={selectedEmployee}
        />
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ApartmentOutlined />
          <span>Client Confidential Data Access</span>
        </div>
      )
    }
  >
  {selectedEmployeeId && selectedEmployee ? (
      <div style={{ padding: "16px" }}>
        {selectedEmployee.client.map((c) => (
          <div key={c.NameOfClient} style={{ marginBottom: 8 }}>
            <Checkbox
              checked={clientAccessMap[c.NameOfClient]}
              onChange={(e) =>
                onToggleClientAccess(
                  selectedEmployeeId,
                  c.NameOfClient,
                  e.target.checked
                )
              }
            >
              {c.NameOfClient}
            </Checkbox>
          </div>
        ))}
      </div>
    ) : (
      <Empty
        description="Select an employee to manage confidential client data access"
        style={{ padding: "60px 0" }}
      />
    )}
  </Card>
));

const EmployeeListItem = React.memo(({ employee, isSelected, onClick }) => (
  <div style={styles.employeeListItem(isSelected)} onClick={onClick}>
    <Space size={12}>
      <Avatar
        size={32}
        src={employee.image}
        style={{ background: "#1677ff" }}
      >
        {employee.firstname?.charAt(0)}
      </Avatar>
      <div>
        <Text strong style={{ color: "inherit", display: "block" }}>
          {employee.firstname} {employee.lastname}
        </Text>
        <Text style={{ fontSize: 12, color: isSelected ? "rgba(255,255,255,0.8)" : "#8c8c8c" }}>
          {employee.designation || employee.role}
        </Text>
      </div>
    </Space>
    {/* <Badge
      count={accessCount}
      style={{
        backgroundColor: isSelected ? "rgba(255,255,255,0.25)" : "#f0f0f0",
        color: isSelected ? "#fff" : "#595959",
        boxShadow: "none"
      }}
    /> */}
  </div>
));

const EmployeeHeader = React.memo(({ employee }) => {

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Avatar
        size={40}
        src={employee.image}
        style={{ background: "#1677ff" }}
      >
        {employee.firstname?.charAt(0)}
      </Avatar>
      <div>
        <div style={{ fontWeight: 600, fontSize: 15 }}>
          {employee.firstname} {employee.lastname}
        </div>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {employee.designation || employee.role} • {employee.department}
        </Text>
      </div>
    </div>
  )
});

const SaveFooter = React.memo(({ onSave, saving }) => (
  <Card style={{ ...styles.mainCard, marginTop: 20 }}>
    <div style={styles.footer}>
      <Space>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1677ff" }} />
        <Text type="secondary">Changes will be saved to employee clients confidential data permissions</Text>
      </Space>
      <AntdButton
        type="primary"
        size="large"
        icon={<SaveOutlined />}
        onClick={onSave}
        loading={saving}
        label="Save Access Settings"
      />
    </div>
  </Card>
));

const styles = {
  container: {
    minHeight: "100vh",
    background: "#f5f5f5",
    padding: "24px"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24
  },
  mainCard: {
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "none"
  },
  listCard: {
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "none",
    height: "fit-content"
  },
  detailCard: {
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "none"
  },
  employeeListItem: (isSelected) => ({
    padding: "12px 16px",
    marginBottom: 8,
    borderRadius: 8,
    cursor: "pointer",
    background: isSelected ? "#1677ff" : "#fff",
    color: isSelected ? "#fff" : "#333",
    border: "none",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.2s"
  }),
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
};